'use es6';

import { createDndAreaColumnElement, resizeDndAreaColumnElement, setDndAreaColumnId } from './renderColumns';
import { createDndAreaRowElement, getLayerChildren, setDndAreaRowId, setDndAreaRowTreeId } from './renderRows';
import { emptyChildrenFromElement } from '../dom/manipulators';
import { getDndAreaElement, getFirstChildElementOfColumn, isDndAreaColumn, isDndAreaRow, isDndAreaEmptyPlaceholderElement, getDndAreaElementForNode, isStaticSectionElement } from '../dom/dndAreaDomSelectors';
import { forEachTree } from '../layoutTreeIterators';
const DOM_LOOKUP_MAP = {};
const getElementFromExistingDomLookup = (treeId, id) => {
  const lookupForTree = DOM_LOOKUP_MAP[treeId];
  if (lookupForTree) {
    const result = lookupForTree[id];
    delete lookupForTree[id];
    return result;
  }
  return null;
};

// Only strip children if this is a row/column that contains more rows/columns
const stripChildrenFromTreeNode = (element, isRow, isModule, name) => {
  if (element) {
    let elementChild = element.firstElementChild;
    // Loop through children, by replacing, and removing elementChild until there are none left.
    if (elementChild) {
      // Account for wrapping_html on columns
      if (!isRow && !isDndAreaColumn(element)) {
        elementChild = getFirstChildElementOfColumn(elementChild);
      }
      const parentOfChildrenToRemove = elementChild.parentElement;

      // If the elementChild is an empty placeholder element, only remove children
      // from the parent that are _not_ the empty placeholder element.
      if (isDndAreaEmptyPlaceholderElement(elementChild)) {
        for (const child of parentOfChildrenToRemove.childNodes) {
          if (child.hasAttribute &&
          // Otherwise we match comment nodes.
          !isDndAreaEmptyPlaceholderElement(child)) {
            parentOfChildrenToRemove.removeChild(child);
          }
        }
      } else if (isDndAreaRow(elementChild) || isStaticSectionElement(elementChild)) {
        emptyChildrenFromElement(parentOfChildrenToRemove);
      } else if (isDndAreaColumn(elementChild.firstElementChild)) {
        emptyChildrenFromElement(parentOfChildrenToRemove.firstElementChild);
      } else if (isModule) {
        const childrenToRemove = [];
        const childrenOfParent = parentOfChildrenToRemove.children;
        for (let i = 0; i < childrenOfParent.length; i++) {
          if (childrenOfParent[i].id.includes(name)) {
            childrenToRemove.push(childrenOfParent[i]);
          }
        }
        childrenToRemove.forEach(childNode => {
          parentOfChildrenToRemove.removeChild(childNode);
        });
      }
    }
  }
  return element;
};

// TODO: Split this into three functions
const findOrCreateDndAreaElement = (treeRootName, node, parentElementToAppendTo, config) => {
  if (!parentElementToAppendTo) {
    return null;
  }
  if (node.isStaticSection()) {
    // Simple case, create a new row element and return it
    const staticSectionElement = getElementFromExistingDomLookup(treeRootName, node.getName()) || config.getStaticSectionFromCacheByName(node.getName());
    parentElementToAppendTo.appendChild(staticSectionElement);
    return staticSectionElement;
  }
  if (node.isEmbed()) {
    const embedElement = getElementFromExistingDomLookup(treeRootName, node.getName()) || config.getEmbedFromCacheByName(node.getName());
    parentElementToAppendTo.appendChild(embedElement);
    return embedElement;
  }
  if (node.isStaticSectionModule()) {
    const moduleElement = getElementFromExistingDomLookup(treeRootName, node.getName());
    return moduleElement;
  }
  if (node.isRow()) {
    const parentCell = node.getParent();
    // Simple case, create a new row element and return it
    const rowElement = getElementFromExistingDomLookup(treeRootName, node.getName()) || createDndAreaRowElement({
      isSection: parentCell.isRoot(),
      rowParams: node.hasValue() ? node.getValue() : null
    });
    stripChildrenFromTreeNode(rowElement, node.isRow());
    parentElementToAppendTo.appendChild(rowElement);
    return rowElement;
  }
  // This is a _cell_, create the column wrapper
  const columnElement = getElementFromExistingDomLookup(treeRootName, node.getName()) || createDndAreaColumnElement({
    treeId: node.getName(),
    type: node.isModule() ? '' : 'cell',
    cellParams: node.getParams()
  });
  // Do not strip children from tree node if this node is a module
  if (!node.isModule()) {
    stripChildrenFromTreeNode(columnElement, node.isRow(), node.isModule(), node.getName());
  }
  if (node.isModule() && config.getModuleElementFromCacheByName) {
    const domNodeCacheEntry = config.getModuleElementFromCacheByName(node.getName());
    // do not strip children from tree node if we can't find the cache entry
    // AND we're ignoring cache misses
    if (domNodeCacheEntry || !config.ignoreCacheMiss) {
      stripChildrenFromTreeNode(columnElement, node.isRow(), node.isModule(), node.getName());
    }
    if (domNodeCacheEntry && !columnElement.contains(domNodeCacheEntry)) {
      columnElement.appendChild(domNodeCacheEntry);
    } else if (!domNodeCacheEntry && !config.ignoreCacheMiss) {
      console.warn('Expected node in cache but not found:', node);
    }
    const nodeParams = node.getParams();
    if (config.renderModuleWithWrappingHTML && !!nodeParams && !!nodeParams.wrapping_html) {
      // Complicated case, render the wrapper HTML for this module and insert this column into
      // the right spot in the wrapper html, and append the entire tree to the parent element
      config.renderModuleWithWrappingHTML(node.getName(), columnElement, nodeParams.wrapping_html, parentElementToAppendTo);
      return columnElement;
    } else {
      // Simple case, append this column wrapper to the parent
      parentElementToAppendTo.appendChild(columnElement);
      return columnElement;
    }
  }
  const nodeParams = node.getParams();
  if (config.renderModuleWithWrappingHTML && !!nodeParams && !!nodeParams.wrapping_html) {
    // Complicated case, render the wrapper HTML for this module and insert this column into
    // the right spot in the wrapper html, and append the entire tree to the parent element
    config.renderModuleWithWrappingHTML(node.getName(), columnElement, nodeParams.wrapping_html, parentElementToAppendTo);
    return columnElement;
  }
  // Simple case, append this column wrapper to the parent
  parentElementToAppendTo.appendChild(columnElement);
  return columnElement;
};
const renderDndAreaLayer = (treeId, treeItems, parentElement, parentColumnId, config) => {
  if (parentElement) {
    for (let i = 0; i < treeItems.length; i++) {
      const treeItem = treeItems[i];
      const isRow = treeItem.isRow();
      const isStaticSectionModule = treeItem.isStaticSectionModule();
      let parentElementToAppendTo = parentElement;
      // No need to add, as static sections come in a batch
      if (!isRow && !isStaticSectionModule) {
        parentElementToAppendTo = parentElement.firstElementChild;
      }
      const layerElement = findOrCreateDndAreaElement(treeId, treeItem, parentElementToAppendTo, config);
      if (isStaticSectionModule) {
        if (!layerElement) {
          //  After first render the static section module is on the cache of `getStaticSectionFromCacheByName`
          return;
        }
        // Static sections don't come back with the `data-hs-cell-id` attribute
        setDndAreaColumnId(layerElement, treeItem.getName());
      } else if (isRow) {
        // Static sections are also rows, and need both ids below.
        setDndAreaRowId(layerElement, parentColumnId, i);
        setDndAreaRowTreeId(layerElement, parentColumnId, treeItem.getName());
      } else {
        setDndAreaColumnId(layerElement, treeItem.getName());
        resizeDndAreaColumnElement(layerElement, treeItem.getWidth());
        parentColumnId = treeItem.getName();
      }
      const layerChildren = getLayerChildren(treeItem);
      if (layerChildren.length > 0) {
        renderDndAreaLayer(treeId, layerChildren, layerElement, parentColumnId, config);
      }
    }
  }
};
const addTreeElementsToLookupMap = (treeId, treeRootEl, treeItems, parentColumnId) => {
  for (let i = 0; i < treeItems.length; i++) {
    const treeItem = treeItems[i];
    const isRow = treeItem.isRow();
    const element = getDndAreaElementForNode(treeItem);
    if (element) {
      DOM_LOOKUP_MAP[treeId][treeItem.getName()] = element;
    }
    const layerChildren = getLayerChildren(treeItem);
    if (layerChildren.length > 0) {
      parentColumnId = isRow ? parentColumnId : treeItem.getName();
      addTreeElementsToLookupMap(treeId, treeRootEl, layerChildren, parentColumnId);
    }
  }
};

/**
 * Walks trees and modifies page DOM to match the tree structure
 * @param {Object} layoutTrees
 * @param {Object} prevLayoutTrees
 * @param {Object} config
 *   @prop {func} getModuleElementFromCacheByName
 *   @prop {func} getStaticSectionFromCacheByName
 *   @prop {func} renderModuleWithWrappingHTML
 */
export const renderDndAreasFromLayoutTrees = (layoutTrees, prevLayoutTrees, config = {}) => {
  // Add all elements to the lookup map (that are currently rendered)
  forEachTree(prevLayoutTrees, (tree, key) => {
    const root = tree.getRootCell();
    const treeRootEl = getDndAreaElement(root.getName());
    DOM_LOOKUP_MAP[key] = {};
    addTreeElementsToLookupMap(root.getName(), treeRootEl, root.getRows(), root.getName());
  });
  // Iterates over the new tree and uses either uses
  //  DOM_LOOK_UP_MAP or creates new elements.
  forEachTree(layoutTrees, tree => {
    const root = tree.getRootCell();
    let treeElement = getDndAreaElement(root.getName());
    treeElement = stripChildrenFromTreeNode(treeElement);
    renderDndAreaLayer(root.getName(), root.getRows(), treeElement, root.getName(), config);
  });
};